// ─────────────────────────────────────────────────────────────────────────────
// EquipmentSection — Janela flutuante de equipamentos do personagem.
//
// Funcionalidades:
// 1. Exibe 5 slots principais: Capacete, Blusa, Calça, Mão Direita, Mão Esquerda
// 2. Permite equipar itens da mochila arrastando ou clicando "Equipar"
// 3. Mostra bônus de atributos (fixos e percentuais) de cada item
// 4. Exibe barra de durabilidade para equipamentos destrutíveis
// 5. Permite desequipar itens (voltam à mochila)
// 6. Permite usar consumíveis com efeitos temporários
// 7. Todos os textos respeitam o idioma selecionado (i18n)
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../../i18n/useTranslation';
import { equipmentApi } from '../../api/equipmentApi';
import { useDockDrag } from './useDockDrag';
const equipmentIcon = '/icons/equipment.svg';
import styles from './EquipmentSection.module.css';

// ─────────────────────────────────────────────────────────────────────────────
// Tipos locais (espelham a resposta da API /api/user/inventory)
// ─────────────────────────────────────────────────────────────────────────────

/** Definição completa de um item (vinda do backend) */
interface ItemDef {
  id: string;
  name: string;
  slot: string;
  item_type?: string;        // 'equipment' | 'consumable' | 'material'
  icon_url?: string;
  description?: string;
  min_level?: number;
  max_durability?: number;   // 0 = indestrutível
  current_durability?: number; // Durabilidade atual (apenas quando equipado)
  bonuses?: Array<{
    attribute: string;
    value: number;
    type?: string;           // 'flat' | 'percent'
  }>;
  temporary_effects?: Array<{
    attribute: string;
    value: number;
    duration_minutes: number;
  }>;
}

/** Entrada no inventário (mochila) */
interface InventoryEntry {
  item: ItemDef;
  quantity: number;
  current_durability?: number;
}

/** Resposta completa do endpoint GET /api/user/inventory */
interface InventoryResponse {
  inventory: InventoryEntry[];
  equipment: Record<string, ItemDef>;
  kamas: number;
  capacity: number;
}

/** Props do componente */
interface EquipmentSectionProps {
  /** Token JWT de autenticação */
  token: string;
  /** Ícone pequeno (só imagem), para a barra compacta do Hub. */
  compact?: boolean;
  /** Janela "encaixada" abaixo do painel da cidade (Hub): sem escurecer o
   *  fundo, compacta, posicionada em `dockTop`. */
  dock?: boolean;
  dockTop?: number;
  /** Modo CONTROLADO pelo pai (Hub): sem botão próprio; abre/fecha por props. */
  controlled?: { open: boolean; onClose: () => void };
}

// ─────────────────────────────────────────────────────────────────────────────
// Constantes: Definição dos 5 slots de equipamento
// ─────────────────────────────────────────────────────────────────────────────

/** Configuração de cada slot de equipamento com ícone e chave i18n */
const EQUIPMENT_SLOTS = [
  { key: 'Head',       icon: '🪖', i18nKey: 'equipment.slot.head'       },
  { key: 'Body',       icon: '👕', i18nKey: 'equipment.slot.body'       },
  { key: 'Legs',       icon: '👖', i18nKey: 'equipment.slot.legs'       },
  { key: 'Right_Hand', icon: '⚔️', i18nKey: 'equipment.slot.rightHand' },
  { key: 'Left_Hand',  icon: '🛡️', i18nKey: 'equipment.slot.leftHand'  },
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// Componente Principal
// ─────────────────────────────────────────────────────────────────────────────

export default function EquipmentSection({ token, compact = false, dock = false, dockTop = 200, controlled }: EquipmentSectionProps) {
  const { t } = useTranslation();

  // ── Estado da modal ──────────────────────────────────────────────────────
  /** Controla se a modal está aberta */
  const [isOpenLocal, setIsOpen] = useState(false);
  const isOpen = controlled ? controlled.open : isOpenLocal;
  const dragCtl = useDockDrag(isOpen);
  /** Controla se o botão está em hover */
  const [isHovered, setIsHovered] = useState(false);
  /** Controla animação de entrada/saída da modal */
  const [isAnimOpen, setIsAnimOpen] = useState(false);
  /** Timer para delay de fechamento (para permitir animação de saída) */
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Dados do inventário ─────────────────────────────────────────────────
  /** Dados completos do inventário (mochila + equipamento) */
  const [data, setData] = useState<InventoryResponse | null>(null);
  /** Estado de carregamento */
  const [loading, setLoading] = useState(false);
  /** Mensagem de erro */
  const [error, setError] = useState<string | null>(null);

  // ── Estado de interação ─────────────────────────────────────────────────
  /** Item selecionado na lista da mochila (para ver detalhes e equipar) */
  const [selectedItem, setSelectedItem] = useState<InventoryEntry | null>(null);
  /** Mensagem de sucesso temporária */
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  /** Mensagem de erro de ação temporária */
  const [actionError, setActionError] = useState<string | null>(null);
  /** Flag de ação em progresso (evita double-click) */
  const [acting, setActing] = useState(false);

  // ── Atalho de teclado: Esc para fechar ─────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ─────────────────────────────────────────────────────────────────────────
  // Funções de carregamento de dados
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Carrega os dados do inventário e equipamento do servidor.
   * Chamado ao abrir a modal e após cada ação (equipar/desequipar/usar).
   */
  const fetchInventory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch('/api/user/inventory', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(t('inventory.error_load'));
      const json: InventoryResponse = await res.json();
      setData(json);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  // Carrega os dados ao montar
  useEffect(() => {
    fetchInventory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Handlers de abertura/fechamento da modal
  // ─────────────────────────────────────────────────────────────────────────

  /** Abre a modal de equipamentos */
  const handleOpen = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setIsOpen(true);
    setIsAnimOpen(true);
    fetchInventory(); // Recarrega dados ao abrir
  };

  /** Fecha a modal com animação de saída */
  const handleClose = () => {
    if (controlled) { setSelectedItem(null); setSuccessMsg(null); setActionError(null); controlled.onClose(); return; }
    setIsAnimOpen(false);
    setIsHovered(false);
    closeTimer.current = setTimeout(() => {
      setIsOpen(false);
      setSelectedItem(null);
      setSuccessMsg(null);
      setActionError(null);
    }, 220); // Duração da animação de saída
  };

  // Modo controlado: recarrega ao abrir.
  useEffect(() => {
    if (controlled?.open) fetchInventory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlled?.open]);

  // ─────────────────────────────────────────────────────────────────────────
  // Limpa mensagens de feedback após 3 segundos
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!successMsg) return;
    const timer = setTimeout(() => setSuccessMsg(null), 3000);
    return () => clearTimeout(timer);
  }, [successMsg]);

  useEffect(() => {
    if (!actionError) return;
    const timer = setTimeout(() => setActionError(null), 4000);
    return () => clearTimeout(timer);
  }, [actionError]);

  // ─────────────────────────────────────────────────────────────────────────
  // Ações do jogador
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Equipa um item da mochila no slot correspondente.
   * Ao equipar:
   * 1. O item sai da mochila
   * 2. Se já havia um item no slot, ele volta para a mochila (swap)
   * 3. Os bônus do item são aplicados ao personagem
   */
  const handleEquip = async (itemId: string) => {
    if (acting) return;
    setActing(true);
    setActionError(null);
    try {
      await equipmentApi.equip(token, itemId);
      setSuccessMsg(t('equipment.equipped'));
      setSelectedItem(null);
      await fetchInventory(); // Recarrega para refletir a mudança
    } catch (err: any) {
      setActionError(err.message);
    } finally {
      setActing(false);
    }
  };

  /**
   * Desequipa um item de um slot e devolve à mochila.
   * Valida se há espaço na mochila antes de desequipar.
   */
  const handleUnequip = async (slot: string) => {
    if (acting) return;
    setActing(true);
    setActionError(null);
    try {
      await equipmentApi.unequip(token, slot);
      setSuccessMsg(t('equipment.unequipped'));
      await fetchInventory();
    } catch (err: any) {
      setActionError(err.message);
    } finally {
      setActing(false);
    }
  };

  /**
   * Usa um item consumível (ex: poção de vida).
   * O item é destruído e o efeito temporário é aplicado ao personagem.
   */
  const handleUseItem = async (itemId: string) => {
    if (acting) return;
    setActing(true);
    setActionError(null);
    try {
      const result = await equipmentApi.useItem(token, itemId);
      setSuccessMsg(result.message || t('equipment.itemUsed'));
      setSelectedItem(null);
      await fetchInventory();
    } catch (err: any) {
      setActionError(err.message);
    } finally {
      setActing(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Funções auxiliares
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Traduz o nome do item usando o sistema i18n.
   * Tenta a chave `item.<slug>.name`; se não existir, usa o nome original.
   */
  const getItemName = (item: ItemDef): string => {
    const baseId = item.icon_url
      ? item.icon_url.split('/').pop()?.replace(/\?.*$/, '').replace('.png', '')
      : item.id ?? item.name.toLowerCase().replace(/\s+/g, '_');
    const key = `item.${baseId}.name` as any;
    const translated = t(key);
    return translated && translated !== '' ? translated : item.name;
  };

  /**
   * Traduz o nome de um atributo para exibição nos bônus.
   * Ex: 'strength' → 'Força' (pt) ou 'Strength' (en)
   */
  const getAttrName = (attr: string): string => {
    const key = `attr.${attr}` as any;
    const translated = t(key);
    return translated && translated !== '' ? translated : attr;
  };

  /**
   * Traduz o nome de um slot de equipamento.
   * Ex: 'Head' → 'Capacete' (pt) ou 'Helmet' (en)
   */
  const getSlotName = (slotKey: string): string => {
    const def = EQUIPMENT_SLOTS.find(s => s.key === slotKey);
    if (def) {
      const translated = t(def.i18nKey as any);
      return translated && translated !== '' ? translated : slotKey;
    }
    return slotKey;
  };

  /**
   * Calcula a cor da barra de durabilidade com base na percentagem.
   * Verde (>60%) → Amarelo (30-60%) → Vermelho (<30%)
   */
  const getDurabilityColor = (current: number, max: number): string => {
    const pct = max > 0 ? (current / max) * 100 : 100;
    if (pct > 60) return '#40c070';      // Verde
    if (pct > 30) return '#e0a020';      // Amarelo
    return '#e04040';                     // Vermelho
  };

  /**
   * Filtra os itens da mochila que são equipáveis (tipo 'equipment' ou slots conhecidos).
   * Exclui materiais e mostra consumíveis separadamente.
   */
  const getEquipableItems = (): InventoryEntry[] => {
    if (!data) return [];
    return data.inventory.filter(entry => {
      const itemType = entry.item.item_type || 'equipment';
      const slot = entry.item.slot;
      // Mostra equipamentos e consumíveis, não mostra materiais
      return itemType !== 'material' && slot !== 'Material';
    });
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Renderização
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Renderiza um slot de equipamento individual.
   * Mostra o item equipado (com ícone, nome e durabilidade) ou o slot vazio.
   */
  const renderSlot = (slotDef: typeof EQUIPMENT_SLOTS[number]) => {
    /** Item atualmente equipado neste slot (null se vazio) */
    const equippedItem = data?.equipment[slotDef.key] ?? null;

    return (
      <div
        key={slotDef.key}
        className={`${styles.equipSlot} ${equippedItem ? styles.equipSlotFilled : ''}`}
        onClick={() => {
          // Se há item equipado, seleciona para ver detalhes
          if (equippedItem) {
            setSelectedItem({ item: equippedItem, quantity: 1 });
          }
        }}
      >
        {/* Label do slot (ex: "CAPACETE") */}
        <span className={styles.slotLabel}>{getSlotName(slotDef.key)}</span>

        {equippedItem ? (
          // ── Slot preenchido: mostra o item equipado ──
          <>
            {equippedItem.icon_url ? (
              <img
                src={`${equippedItem.icon_url}?v=4`}
                alt={getItemName(equippedItem)}
                className={styles.slotItemIcon}
                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <div className={styles.slotItemPlaceholder}>
                {getItemName(equippedItem).charAt(0).toUpperCase()}
              </div>
            )}
            <span className={styles.slotItemName}>{getItemName(equippedItem)}</span>

            {/* Barra de durabilidade (apenas se o item tem durabilidade > 0) */}
            {equippedItem.max_durability && equippedItem.max_durability > 0 && (
              <>
                <div className={styles.durabilityBar}>
                  <div
                    className={styles.durabilityFill}
                    style={{
                      width: `${Math.max(0, ((equippedItem.current_durability ?? equippedItem.max_durability) / equippedItem.max_durability) * 100)}%`,
                      background: getDurabilityColor(
                        equippedItem.current_durability ?? equippedItem.max_durability,
                        equippedItem.max_durability
                      ),
                    }}
                  />
                </div>
                <span className={styles.durabilityText}>
                  {equippedItem.current_durability ?? equippedItem.max_durability}/{equippedItem.max_durability}
                </span>
              </>
            )}

            {/* Botão de desequipar */}
            <button
              className={styles.unequipBtn}
              onClick={(e) => {
                e.stopPropagation();
                handleUnequip(slotDef.key);
              }}
              disabled={acting}
            >
              {t('equipment.unequip')}
            </button>
          </>
        ) : (
          // ── Slot vazio: mostra ícone placeholder ──
          <span className={styles.slotEmptyIcon}>{slotDef.icon}</span>
        )}
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render principal
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <>
      {controlled ? null : compact ? (
        <button className={styles.compactBtn} onClick={handleOpen} title={t('equipment.title')} aria-label={t('equipment.title')}>
          <img src={equipmentIcon} alt="" className={styles.compactImg} />
        </button>
      ) : (
      /* ── Botão de equipamento no card do personagem ────────────────────── */
      <button
        className={styles.equipButton}
        onClick={handleOpen}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title={t('equipment.title')}
        aria-label={t('equipment.title')}
      >
        <div className={styles.imgWrap}>
          <img
            src={equipmentIcon}
            alt={t('equipment.title')}
            className={`${styles.equipIcon} ${isHovered ? styles.equipIconHover : ''}`}
          />
        </div>
        <span className={styles.tileLabel}>{t('equipment.title')}</span>
      </button>
      )}

      {/* ── Modal de equipamentos ──────────────────────────────────────────── */}
      {isOpen && createPortal(
        <div
          className={dock ? styles.dock : `${styles.overlay} ${isAnimOpen ? styles.overlayIn : styles.overlayOut}`}
          data-dock-window={dock ? '' : undefined}
          onClick={dock ? undefined : handleClose}
          style={dock ? ({ '--dock-top': `${dockTop}px`, ...dragCtl.style } as React.CSSProperties) : undefined}
        >
          <div
            className={dock
              ? `${styles.modal} ${styles.dockModal}`
              : `${styles.modal} ${isAnimOpen ? styles.modalIn : styles.modalOut}`}
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal={dock ? undefined : 'true'}
            aria-label={t('equipment.title')}
          >
            {/* ── Header ── */}
            <header
              className={`${styles.modalHeader} ${dock ? styles.dockDragHandle : ''}`}
              onPointerDown={dock ? dragCtl.onHeaderPointerDown : undefined}
            >
              <div className={styles.headerLeft}>
                <img src={equipmentIcon} alt="" className={styles.headerImg} />
                <h2 className={styles.modalTitle}>{t('equipment.title')}</h2>
              </div>
              <div className={styles.headerRight}>
                <button className={styles.closeBtn} onClick={handleClose} aria-label={t('common.close')}>✕</button>
              </div>
            </header>

            {/* ── Body ── */}
            <div className={styles.modalBody}>
              {/* Mensagens de feedback */}
              {successMsg && <div className={styles.successMsg}>{successMsg}</div>}
              {actionError && <div className={styles.errorMsg}>{actionError}</div>}

              {/* Estado de carregamento */}
              {loading && (
                <div className={styles.centerState}>
                  <div className={styles.spinner} />
                  <span>{t('common.loading')}</span>
                </div>
              )}

              {/* Estado de erro */}
              {error && <div className={styles.errorBox}>{error}</div>}

              {/* Conteúdo principal */}
              {!loading && !error && data && (
                <>
                  {/* ═══ SEÇÃO 1: Slots de equipamento ═══ */}
                  <div className={styles.slotsLayout}>
                    {/* Linha topo: Capacete */}
                    <div className={styles.slotRowTop}>
                      {renderSlot(EQUIPMENT_SLOTS[0])}
                    </div>

                    {/* Linha central: Mão Esquerda — Blusa — Mão Direita */}
                    <div className={styles.slotRowMiddle}>
                      {renderSlot(EQUIPMENT_SLOTS[4])}  {/* Left_Hand */}
                      {renderSlot(EQUIPMENT_SLOTS[1])}  {/* Body */}
                      {renderSlot(EQUIPMENT_SLOTS[3])}  {/* Right_Hand */}
                    </div>

                    {/* Linha inferior: Calça */}
                    <div className={styles.slotRowBottom}>
                      {renderSlot(EQUIPMENT_SLOTS[2])}
                    </div>
                  </div>

                  {/* ═══ SEÇÃO 2: Itens na mochila ═══ */}
                  <h4 className={styles.sectionTitle}>
                    🎒 {t('equipment.bagTitle')}
                  </h4>

                  {getEquipableItems().length === 0 ? (
                    <div className={styles.emptyBag}>{t('equipment.bagEmpty')}</div>
                  ) : (
                    <div className={styles.bagGrid}>
                      {getEquipableItems().map((entry, idx) => {
                        const name = getItemName(entry.item);
                        const iconSrc = entry.item.icon_url ? `${entry.item.icon_url}?v=4` : null;
                        const selected = selectedItem?.item.id === entry.item.id;
                        const isConsumable = entry.item.item_type === 'consumable' || entry.item.slot === 'Consumable';

                        return (
                          <button
                            key={`${entry.item.id}-${idx}`}
                            className={`${styles.bagItem} ${selected ? styles.bagItemActive : ''}`}
                            onClick={() => setSelectedItem(selected ? null : entry)}
                            title={name}
                          >
                            <div className={styles.bagItemIconWrap}>
                              {iconSrc ? (
                                <img
                                  src={iconSrc}
                                  alt={name}
                                  className={styles.bagItemImg}
                                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                />
                              ) : (
                                <div className={styles.bagItemEmoji}>
                                  {isConsumable ? '🧪' : name.charAt(0).toUpperCase()}
                                </div>
                              )}
                              {entry.quantity > 1 && (
                                <span className={styles.bagItemQty}>×{entry.quantity}</span>
                              )}
                            </div>
                            <span className={styles.bagItemName}>{name}</span>
                            <span className={styles.bagItemSlotTag}>
                              {isConsumable ? t('equipment.consumable') : getSlotName(entry.item.slot)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* ═══ SEÇÃO 3: Detalhe do item selecionado ═══ */}
                  {selectedItem && (
                    <div className={styles.detail}>
                      <div className={styles.detailIcon}>
                        {selectedItem.item.icon_url ? (
                          <img
                            src={`${selectedItem.item.icon_url}?v=4`}
                            alt={getItemName(selectedItem.item)}
                            className={styles.detailImg}
                          />
                        ) : (
                          <div className={styles.detailPlaceholder}>
                            {getItemName(selectedItem.item).charAt(0)}
                          </div>
                        )}
                      </div>

                      <div className={styles.detailInfo}>
                        <h3 className={styles.detailName}>{getItemName(selectedItem.item)}</h3>
                        <span className={styles.detailSlotTag}>
                          {getSlotName(selectedItem.item.slot)}
                        </span>

                        {/* Descrição do item */}
                        {selectedItem.item.description && (
                          <p className={styles.detailDesc}>{selectedItem.item.description}</p>
                        )}

                        {/* Metadados */}
                        <div className={styles.detailMeta}>
                          <span>{t('equipment.level')}: {selectedItem.item.min_level ?? 1}</span>
                          {selectedItem.quantity > 1 && (
                            <span>{t('equipment.quantity')}: ×{selectedItem.quantity}</span>
                          )}
                          {selectedItem.item.max_durability && selectedItem.item.max_durability > 0 && (
                            <span>{t('equipment.durability')}: {selectedItem.item.max_durability}</span>
                          )}
                        </div>

                        {/* Bônus de atributos */}
                        {selectedItem.item.bonuses && selectedItem.item.bonuses.length > 0 && (
                          <div className={styles.bonusList}>
                            {selectedItem.item.bonuses.map((bonus, i) => {
                              const isPercent = bonus.type === 'percent';
                              const isPositive = bonus.value >= 0;
                              return (
                                <span
                                  key={i}
                                  className={`${styles.bonusChip} ${isPositive ? styles.bonusPositive : styles.bonusNegative}`}
                                >
                                  {isPositive ? '+' : ''}{bonus.value}{isPercent ? '%' : ''} {getAttrName(bonus.attribute)}
                                </span>
                              );
                            })}
                          </div>
                        )}

                        {/* Efeitos temporários (consumíveis) */}
                        {selectedItem.item.temporary_effects && selectedItem.item.temporary_effects.length > 0 && (
                          <div className={styles.bonusList}>
                            {selectedItem.item.temporary_effects.map((eff, i) => (
                              <span key={i} className={`${styles.bonusChip} ${styles.bonusPositive}`}>
                                +{eff.value} {getAttrName(eff.attribute)} ({eff.duration_minutes} min)
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Botões de ação */}
                        <div className={styles.actionRow}>
                          {(() => {
                            const isConsumable = selectedItem.item.item_type === 'consumable'
                              || selectedItem.item.slot === 'Consumable';
                            const isEquipment = !isConsumable
                              && selectedItem.item.slot !== 'Material'
                              && selectedItem.item.item_type !== 'material';
                            // Verifica se o item está na mochila (não equipado)
                            const isInBag = data?.inventory.some(e => e.item.id === selectedItem.item.id);

                            return (
                              <>
                                {/* Botão Equipar: apenas para equipamentos na mochila */}
                                {isEquipment && isInBag && (
                                  <button
                                    className={`${styles.equipActionBtn} ${styles.btnEquip} ${acting ? styles.btnDisabled : ''}`}
                                    onClick={() => handleEquip(selectedItem.item.id)}
                                    disabled={acting}
                                  >
                                    {acting ? t('common.loading') : t('equipment.equip')}
                                  </button>
                                )}

                                {/* Botão Usar: apenas para consumíveis na mochila */}
                                {isConsumable && isInBag && (
                                  <button
                                    className={`${styles.equipActionBtn} ${styles.btnUse} ${acting ? styles.btnDisabled : ''}`}
                                    onClick={() => handleUseItem(selectedItem.item.id)}
                                    disabled={acting}
                                  >
                                    {acting ? t('common.loading') : t('equipment.use')}
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
